/*
 * shop5.js - small behaviours of the Shop Five build on top of Warehouse 7.0.1 (docs/build/UI_BUILD_RULES.md
 * sections 1, 4 and 15). Loaded with defer after the vendor theme.js on every page; no dependencies, no third-party
 * code.
 *
 * 0. Parts registry (window.Shop5Parts, stage 6H): a section root marked data-shop5-part="<name>" gets the behaviour
 *    registered under that name once the page is parsed, again when the theme editor loads the section, and its
 *    clean-up when the editor unloads it.
 * 2. Hidden search panels are inert, so keyboard focus never enters a closed drop-down (ITEMS P4-13); the open search
 *    closes when keyboard focus leaves it for the page.
 * 3. Share buttons ([data-shop5-share]: sections/shop5-branches.liquid, the product page since vendor edit PM1): the
 *    system share sheet where the browser has one, else the link is copied and the role=status message beside the
 *    button ([data-shop5-toast]) says so (ITEMS P2-2). 3b: a branch photo of the branches page that does not load gives
 *    way to its letter tile ([data-shop5-photo]).
 * 4. Product page bar (sections/shop5-product-bar.liquid; ITEMS P4-32): shown once the main add-to-cart button has
 *    scrolled up out of view, while the footer is out of view; it works through the main product form. While the add
 *    button sits in the phone tab bar's band, the tab bar steps aside (html.shop5-add-low).
 * 5. Gallery thumbnails (vendor edit GA3; ITEMS P4-21): aria-current follows the shown photo.
 * 6. Phone zoom dialog (the vendor's PhotoSwipe in snippets/product-gallery.liquid): named by the product title, modal,
 *    and its photos described like the gallery's.
 * 9. Buy now (snippets/shop5-buy-now.liquid): adds the chosen size and quantity, then opens the checkout only when the
 *    cart took them; a refused add stays on the page with the cart's message (ITEMS P1-4).
 * 10. Header, menu panels, tab bar and cart toast (stage 6H; sections/shop5-header.liquid, shop5-menu.liquid,
 *    shop5-tabbar.liquid): three parts registered with part 0 ("shop5-header", "shop5-menu", "shop5-tabbar");
 *    window.Shop5Menu and window.Shop5Toast.
 * 11. Footer link lists (stage 6H; sections/shop5-footer.liquid): part "shared-footer", accordions on phones.
 * 12. Language switcher (rules section 18; snippets/shop5-lang-switcher.liquid): part "shop5-lang", the owner's
 *    component - a panel on desktop, a bottom sheet on phones - built on Shopify's localization form.
 * 13. Quick add of the home cards (owner order 2026-10-31): moved from assets/shop5-home.js. Off the home page the card
 *    uses the vendor's buy-button instead (owner ruling 2026-11-01). One listener on the document, so it also reaches a
 *    rotating home row.
 * Retired in stage 6H (owner home design): 1 scroll-to-top button, 7 home banners (sections/shop5-hero.liquid),
 * 8 home product tabs (sections/shop5-product-tabs.liquid). The numbers are not reused. The home sections register
 * with part 0 from assets/shop5-home.js (home template only).
 */

// 0. Parts registry. Shop5Parts.register(name, init) runs init(root) for every element marked data-shop5-part="name"
//    once the document is parsed (at once when it already is: this file is deferred), keeps the clean-up function
//    init returns, and follows the theme editor: shopify:section:load starts the parts inside the loaded section,
//    shopify:section:unload runs the clean-up of the parts inside the section going away. A part starts at most once
//    per element; an error in one part is reported and does not stop the others.
(function () {
  'use strict';

  if (window.Shop5Parts) return;

  var inits = {};
  var running = [];
  var ready = document.readyState !== 'loading';

  var report = function (name, error) {
    if (window.console && window.console.error) window.console.error('Shop5Parts ' + name + ':', error);
  };

  var roots = function (scope, name) {
    var selector = '[data-shop5-part="' + name + '"]';
    var found = Array.prototype.slice.call(scope.querySelectorAll ? scope.querySelectorAll(selector) : []);
    if (scope.matches && scope.matches(selector)) found.unshift(scope);
    return found;
  };

  var isRunning = function (root, name) {
    for (var i = 0; i < running.length; i += 1) {
      if (running[i].root === root && running[i].name === name) return true;
    }
    return false;
  };

  var start = function (scope, name) {
    roots(scope, name).forEach(function (root) {
      if (isRunning(root, name)) return;
      var destroy = null;
      try {
        destroy = inits[name](root);
      } catch (error) {
        report(name, error);
      }
      running.push({ root: root, name: name, destroy: typeof destroy === 'function' ? destroy : null });
    });
  };

  var startAll = function (scope) {
    Object.keys(inits).forEach(function (name) {
      start(scope, name);
    });
  };

  // Stops the parts whose root is inside the scope (the whole document when the scope is null: roots no longer in the
  // page are stopped then too).
  var stop = function (scope) {
    running = running.filter(function (entry) {
      var inside = scope ? scope === entry.root || scope.contains(entry.root) : !document.documentElement.contains(entry.root);
      if (!inside) return true;
      if (entry.destroy) {
        try {
          entry.destroy();
        } catch (error) {
          report(entry.name, error);
        }
      }
      return false;
    });
  };

  window.Shop5Parts = {
    register: function (name, init) {
      if (typeof init !== 'function' || inits[name]) return;
      inits[name] = init;
      if (ready) start(document, name);
    },
    start: function (scope) {
      startAll(scope || document);
    },
    stop: function (scope) {
      stop(scope || null);
    }
  };

  if (!ready) {
    document.addEventListener('DOMContentLoaded', function () {
      ready = true;
      startAll(document);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    stop(null);
    if (event.target) startAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target) stop(event.target);
  });
})();

(function () {
  'use strict';

  // 2. Hidden search panels are inert (ITEMS P4-13).
  var syncInert = function (panel) {
    if (panel.getAttribute('aria-hidden') === 'true') panel.setAttribute('inert', '');
    else panel.removeAttribute('inert');
  };
  Array.prototype.forEach.call(document.querySelectorAll('.search-bar__results'), function (panel) {
    syncInert(panel);
    new MutationObserver(function () {
      syncInert(panel);
    }).observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
  });

  // The vendor opens the search on focus in its field (is-fixed; on phones a panel over the whole screen, and the page
  // locked) and closes it only on its Cancel button, or on a click elsewhere from 641 px. Keyboard focus that moved on
  // to the page left it open, covering every element focused after it on phones (stage 4, fix loop 2). When focus
  // leaves the search for another element of the page, the vendor's own Cancel action closes it; a focus loss to no
  // element (a tap on the panel itself, the window losing focus) keeps it open.
  Array.prototype.forEach.call(document.querySelectorAll('.header__search-bar-wrapper'), function (wrapper) {
    wrapper.addEventListener('focusout', function (event) {
      var next = event.relatedTarget;
      if (!next || wrapper.contains(next) || !wrapper.classList.contains('is-fixed')) return;
      var cancel = wrapper.querySelector('[data-action="unfix-search"]');
      if (cancel) cancel.click();
    });
  });

  // A role=status message ([data-shop5-toast]) shows the text for 2.5 s; each message keeps its own timers. The text
  // stays while the message slides out (the branches toast leaves in 0.4 s) and is cleared after, so the next copy
  // is announced again.
  var say = function (toast, text) {
    if (!toast) return;
    window.clearTimeout(toast.shop5Timer);
    window.clearTimeout(toast.shop5Clear);
    toast.textContent = text;
    toast.classList.add('is-visible');
    toast.shop5Timer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
      toast.shop5Clear = window.setTimeout(function () {
        toast.textContent = '';
      }, 450);
    }, 2500);
  };

  // 3. Share buttons ([data-shop5-share]: sections/shop5-branches.liquid, snippets/product-meta.liquid since vendor
  //    edit PM1): the system share sheet where the browser has one, else the link is copied and the role=status
  //    message beside the button ([data-shop5-toast], else the page's first one) says so (ITEMS P2-2). One listener
  //    on the document, so a button drawn after the page load works too. A button may add the share sheet's text
  //    (data-share-text; the branches page passes the live page's pin and branch name). When the copy fails, the
  //    product page opens the link in a new window (stage 3); the branches page stays silent like the live page's
  //    shareIt() (rules section 16 D, decision D7): its links are Google Maps places, not a page to open.
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-shop5-share]') : null;
    if (!button) return;
    event.preventDefault();
    var toast = (button.parentNode && button.parentNode.querySelector('[data-shop5-toast]')) || document.querySelector('[data-shop5-toast]');
    var title = button.getAttribute('data-share-title') || document.title;
    var url = button.getAttribute('data-share-url') || window.location.href;
    var text = button.getAttribute('data-share-text');
    var fallback = button.closest('.shop5-branches') ? function () {} : function () {
      window.open(url, '_blank', 'noopener');
    };
    if (navigator.share) {
      var data = { title: title, url: url };
      if (text) data.text = text;
      navigator.share(data).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        say(toast, toast ? toast.getAttribute('data-copied') : '');
      }, fallback);
      return;
    }
    fallback();
  });

  // 3b. Branch photos of the branches page ([data-shop5-photo], sections/shop5-branches.liquid): a photo that does not
  //     load gives way to the branch's letter tile printed beside it, as the live page's inline error handler did
  //     (rules section 16 D). A photo that failed before this script ran is caught by its state.
  Array.prototype.forEach.call(document.querySelectorAll('[data-shop5-photo]'), function (wrap) {
    var img = wrap.querySelector('img');
    if (!img) return;
    var broken = function () { wrap.classList.add('is-broken'); };
    if (img.complete && img.naturalWidth === 0 && img.currentSrc) broken();
    else img.addEventListener('error', broken);
  });

  // 4. Product page bar (sections/shop5-product-bar.liquid; ITEMS P4-32). The bar has no form of its own: its stepper
  //    and the main form's stepper keep one quantity, and its button presses the main form's add button (the vendor's
  //    buy-button element finds the form from the pressed button), so adding, the mini cart and error messages are
  //    the vendor's. It shows as soon as the main add button starts going under the sticky header (or above the
  //    screen) and hides again once the button is back wholly in view, so there is no scroll position where the button
  //    is covered and the bar closed; it stays hidden while the footer is in view. At load, with the add button
  //    still below the first screen, it stays hidden: there it would lie over the sizes, which come before the add
  //    button, and its button would add a size the shopper has not seen.
  var bar = document.querySelector('[data-shop5-product-bar]');
  var form = bar && document.querySelector('[data-section-type="product"] form.product-form');
  if (bar && !form) bar.parentNode.removeChild(bar);
  if (bar && form && 'IntersectionObserver' in window) {
    var barAdd = bar.querySelector('[data-shop5-bar-add]');
    var barQuantity = bar.querySelector('[data-shop5-bar-quantity]');
    var barPrice = bar.querySelector('[data-shop5-bar-price]');
    var footer = document.getElementById('shop5-footer');
    var mainAdd = function () {
      return form.querySelector('.product-form__add-button');
    };
    var mainQuantity = function () {
      return form.querySelector('[name="quantity"]');
    };
    var sync = function () {
      var add = mainAdd();
      if (barAdd && add) {
        // Sold out, or the vendor's add request in flight: the bar button waits with the main one.
        barAdd.disabled = add.disabled;
        barAdd.textContent = add.textContent.trim();
      }
      var price = form.querySelector('.price-list .price');
      if (barPrice && price) {
        var copy = price.cloneNode(true);
        Array.prototype.forEach.call(copy.querySelectorAll('.visually-hidden'), function (n) {
          n.parentNode.removeChild(n);
        });
        barPrice.textContent = copy.textContent.trim();
      }
      var quantity = mainQuantity();
      if (barQuantity && quantity && barQuantity.value !== quantity.value) barQuantity.value = quantity.value;
    };
    var addAbove = false;
    var footerInView = false;
    var show = function () {
      var visible = addAbove && !footerInView;
      if (!visible && bar.contains(document.activeElement)) {
        var add = mainAdd();
        if (add) add.focus({ preventScroll: true });
      }
      bar.classList.toggle('is-visible', visible);
      if (visible) document.documentElement.style.setProperty('--s5-bar-h', bar.offsetHeight + 'px');
      document.documentElement.classList.toggle('shop5-bar-open', visible);
      if (visible) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    };
    var watched = null;
    var observer = null;
    var band = -1;
    // The band the sticky header covers at the top of the screen: the header section's real lower edge (its sticky
    // top plus its drawn height, border included - 57 px on phones where --header-height says 56), 0 when it does not
    // stick. The add button is watched in an area that starts at that edge and runs far below the screen, so the
    // button leaves it only upwards: it counts as going away as soon as any part of it is above the edge (threshold 1:
    // no longer wholly inside), however the page got there, and as back once it is wholly inside again. Opening the
    // bar moves nothing above the button, so the state cannot flip back at the edge. The observer is rebuilt when the
    // band changes.
    var header = document.querySelector('.shopify-section__header');
    var observe = function () {
      var next = 0;
      if (header) {
        var hs = getComputedStyle(header);
        if (hs.position === 'sticky' || hs.position === 'fixed') next = Math.ceil(Math.max(0, parseFloat(hs.top) || 0) + header.getBoundingClientRect().height);
      }
      if (observer && next === band) return;
      band = next;
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === watched) addAbove = entry.intersectionRatio < 1 && entry.boundingClientRect.top < entry.rootBounds.top + 0.5;
        });
        show();
      }, { rootMargin: '-' + band + 'px 0px 100000px 0px', threshold: 1 });
      if (watched) observer.observe(watched);
    };
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerInView = entry.isIntersecting;
        });
        show();
      }).observe(footer);
    }
    // The phone tab bar (sections/shop5-tabbar.liquid, 768 px and below) floats over the foot of the screen. While the
    // add button's row reaches into the band it covers (a short product page at load), the tab bar steps aside
    // (html.shop5-add-low, shop5-ui.css), so no sticky element covers the add-to-cart button (rules section 1). The band
    // starts at the tab bar's top edge (a fixed box's offsetTop ignores its hide transform); the observer is rebuilt
    // when that edge or the watched row changes. Where the tab bar is not fixed (above 768 px) nothing is watched.
    var tabbar = document.querySelector('.shop5-tabbar');
    var low = null;
    var lowTop = -1;
    var observeLow = function (force) {
      var next = tabbar && getComputedStyle(tabbar).position === 'fixed' ? tabbar.offsetTop : 0;
      if (!force && next === lowTop) return;
      lowTop = next;
      if (low) low.disconnect();
      low = null;
      if (!lowTop || !watched) {
        document.documentElement.classList.remove('shop5-add-low');
        return;
      }
      low = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === watched) document.documentElement.classList.toggle('shop5-add-low', entry.isIntersecting);
        });
      }, { rootMargin: '-' + lowTop + 'px 0px 0px 0px' });
      low.observe(watched);
    };
    var watch = function () {
      // The add-to-cart button itself (UQ25 line 26); its row only when the vendor draws no add button.
      var target = mainAdd() || form.querySelector('.product-form__payment-container');
      if (!target || target === watched) return;
      if (watched) observer.unobserve(watched);
      watched = target;
      observer.observe(target);
      observeLow(true);
    };
    observe();
    watch();
    var resized = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resized);
      resized = window.setTimeout(function () {
        observe();
        observeLow(false);
      }, 150);
    });
    window.addEventListener('load', function () {
      observe();
      observeLow(false);
    });
    sync();
    // The vendor re-renders the variant block and the buttons after a size change (product:rerender).
    new MutationObserver(function () {
      watch();
      sync();
    }).observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    form.addEventListener('change', function (event) {
      if (event.target && event.target.name === 'quantity') sync();
    });
    if (barQuantity) {
      barQuantity.addEventListener('change', function () {
        var quantity = mainQuantity();
        if (quantity && quantity.value !== barQuantity.value) {
          quantity.value = barQuantity.value;
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    if (barAdd) {
      barAdd.addEventListener('click', function (event) {
        event.preventDefault();
        var add = mainAdd();
        if (!add || add.disabled) return;
        var quantity = mainQuantity();
        if (quantity && barQuantity && quantity.value !== barQuantity.value) quantity.value = barQuantity.value;
        add.click();
      });
    }
    // A refused add (for example more than the stock): the vendor appends its message at the end of the form and slides
    // it open (0.25 s), which can be far below the pressed button or, when the bar's button was pressed further down the
    // page, above the screen, where the page's scroll anchoring can leave it under the sticky header. Once open, the
    // message is brought to the middle of the screen unless it is wholly between the header band and the bar; its
    // words are the vendor's, announced through role alert (vendor edit BA1).
    form.addEventListener('product:added', function (event) {
      if (!event.detail || event.detail.productAdded !== false) return;
      var earlier = Array.prototype.slice.call(form.querySelectorAll('.cart__status-message'));
      var tries = 0;
      var look = function () {
        var message = Array.prototype.filter.call(form.querySelectorAll('.cart__status-message'), function (m) {
          return earlier.indexOf(m) === -1;
        })[0];
        if (!message) {
          tries += 1;
          if (tries < 20) window.setTimeout(look, 150);
          return;
        }
        window.setTimeout(function () {
          var r = message.getBoundingClientRect();
          var below = bar.classList.contains('is-visible') ? bar.offsetHeight : 0;
          if (r.top < Math.max(band, 0) || r.bottom > window.innerHeight - below) {
            message.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          }
        }, 400);
      };
      look();
    });
  }

  // 5. Gallery thumbnails (snippets/product-gallery.liquid, vendor edit GA3; ITEMS P4-21): the vendor marks the shown
  //    photo's thumbnail with a class only; screen readers get the same mark as aria-current.
  Array.prototype.forEach.call(document.querySelectorAll('.product-gallery__thumbnail-list'), function (list) {
    var mark = function () {
      Array.prototype.forEach.call(list.querySelectorAll('.product-gallery__thumbnail'), function (thumb) {
        if (thumb.classList.contains('is-nav-selected')) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };
    new MutationObserver(mark).observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
  });

  // 6. Phone zoom dialog (snippets/product-gallery.liquid: the vendor's PhotoSwipe, whose markup the vendor asks to keep
  //    unaltered). The dialog is named by the product title and marked modal; PhotoSwipe draws its photos without an
  //    alt text, so each takes the alt text of the gallery photo it enlarges (found by the zoom address the gallery
  //    photo carries), and the blurred copy shown while it loads an empty one.
  Array.prototype.forEach.call(document.querySelectorAll('[data-section-type="product"] .pswp'), function (dialog) {
    var section = dialog.closest('[data-section-type="product"]');
    var title = section.querySelector('.product-meta__title');
    if (title) dialog.setAttribute('aria-label', title.textContent.trim());
    dialog.setAttribute('aria-modal', 'true');
    var describe = function () {
      Array.prototype.forEach.call(dialog.querySelectorAll('img.pswp__img:not([alt])'), function (img) {
        var source = null;
        if (!img.classList.contains('pswp__img--placeholder')) {
          source = Array.prototype.filter.call(section.querySelectorAll('.product-gallery__image[data-zoom]'), function (photo) {
            return photo.getAttribute('data-zoom') === img.getAttribute('src');
          })[0];
        }
        img.setAttribute('alt', source ? source.getAttribute('alt') || '' : '');
      });
    };
    new MutationObserver(describe).observe(dialog, { subtree: true, childList: true });
  });

  // 9. Buy now (snippets/shop5-buy-now.liquid, vendor edit BB2; rules section 4; UQ22 line 4; ITEMS P1-4). As on the
  //    live theme, the chosen size and quantity go to the page language's cart route, and the checkout (the button's
  //    value) opens only when the cart took them. A refused add (for example more than the stock, a 422 reply) keeps
  //    the shopper on the page with the cart's own words at the end of the form, in the vendor's message box and for
  //    as long as the add button's (5.5 s), announced (role alert). The vendor's product:added event goes first, as
  //    after a refused add from the add button: the vendor refreshes the header count and part 4 brings the message
  //    into view. When the request itself fails, the button submits the form, and Shopify adds and opens the checkout
  //    (return_to). Listened for on the document, so the button of the quick view works as well; a page brought back
  //    from the browser's back-forward cache gets its button back.
  var buyNowReady = function (button) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  };
  // The vendor's box is closed (height 0) until its script opens it; this one opens by its data attribute
  // (shop5-ui.css section 23), at once.
  var buyNowMessage = function (form, words) {
    var section = form.closest('[data-section-type]');
    var box = document.createElement('div');
    box.className = 'cart__status-message cart__status-message--' + (section ? section.getAttribute('data-section-type') : 'product');
    box.setAttribute('data-shop5-buy-now-message', '');
    var alert = document.createElement('div');
    alert.className = 'alert alert--error';
    alert.setAttribute('role', 'alert');
    var line = document.createElement('p');
    line.textContent = words;
    alert.appendChild(line);
    box.appendChild(alert);
    form.appendChild(box);
    window.setTimeout(function () {
      box.remove();
    }, 5500);
  };
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-shop5-buy-now]') : null;
    if (!button || !button.form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    if (button.disabled) return;
    var form = button.form;
    var quantity = form.querySelector('[name="quantity"]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    fetch(window.routes.cartAddUrl + '.js', {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (response.ok) {
          if (window.shop5Checkout) {
            window.shop5Checkout.go(
              function () { window.location.href = button.value; },
              button,
              function () {
                /* sheet closed without saving: restore the button and end the loading state
                   with EXACTLY the statements this handler already uses after a failed add */
                buyNowReady(button);
                document.dispatchEvent(new CustomEvent('theme:loading:end'));
              }
            );
          } else {
            window.location.href = button.value;
          }
          return null;
        }
        return response.json().then(function (reply) {
          if (!reply || typeof reply.description !== 'string' || !reply.description) throw new Error('no cart message');
          buyNowReady(button);
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          button.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { button: button, variant: null, quantity: quantity ? parseInt(quantity.value, 10) || 1 : 1, productAdded: false }
          }));
          buyNowMessage(form, reply.description);
        });
      })
      .catch(function () {
        buyNowReady(button);
        if (form.requestSubmit) {
          form.requestSubmit(button);
          return;
        }
        var back = document.createElement('input');
        back.type = 'hidden';
        back.name = button.name;
        back.value = button.value;
        form.appendChild(back);
        form.submit();
      });
  });
  window.addEventListener('pageshow', function (event) {
    var busy = event.persisted ? document.querySelectorAll('[data-shop5-buy-now][aria-busy]') : [];
    if (!busy.length) return;
    Array.prototype.forEach.call(busy, buyNowReady);
    document.dispatchEvent(new CustomEvent('theme:loading:end'));
  });
})();

// 10. Header, menu panels, tab bar and cart toast (stage 6H; docs/build/parts6h/shared-header.js).
/* ---------------------------------------------------------------- shared header, menus, tab bar, toast (stage 6H) */
/* Owner design scripts (index.html: S5Menu 3648-3673, tab bar and toast 3301-3318), rebuilt per section root for the
   theme editor (spec docs/build/evidence/6H/map/A-header-menu.md sections 3.3, 5.3, 6.3, 7, 8.3, 9, 14.4; F-theme.md
   sections 4.1-4.3). Three parts, one per section:
   - "shop5-header" (sections/shop5-header.liquid): the phone search toggle reports its state (aria-expanded) and
     closes on Escape; the cart count "bumps" when the vendor Cart rewrites it. The vendor HeaderSection, SearchBar and
     Cart classes (theme.js) still run the search, the mini cart and the count.
   - "shop5-menu" (sections/shop5-menu.liquid): the desktop panel (769 px and up) and the phone panel. Openers anywhere
     on the page ([data-shop5-open-menu]: the header button, the tab bar's Categories; [data-shop5-open-search]: the
     tab bar's Search) are caught on the document, so they work across sections. Closed panels are hidden and inert;
     opening removes hidden, then adds .is-open one frame later so the CSS transition runs; closing waits for the
     transition before hiding again. Focus moves into the open panel, is kept there (Tab), and returns to the opener.
     Escape closes (on phone level 2 it goes back first). The phone panel locks the page with the vendor's
     html.is-locked, only when nothing else had locked it. html.shop5-menu-open hides the tab bar.
     window.Shop5Menu = { open({ panel: 'desktop'|'mobile', focusSearch }), close() }.
   - "shop5-tabbar" (sections/shop5-tabbar.liquid): hide on scroll down / show on scroll up after the visitor's first
     own input; the cart badge copies the vendor .header__cart-count (MutationObserver), so the vendor Cart stays the
     one source of truth; window.Shop5Toast = { show(message, { alert }) }.
   Reduced motion: the CSS has no moves; the script does not wait for transitions. Text is set with textContent only; no outside code. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function nextFrame(fn) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(fn);
    });
  }

  function afterTransition(el, fn) {
    if (!el || reduceMotion.matches) {
      fn();
      return;
    }
    var done = false;
    var timer = 0;
    function finish(event) {
      if (event && event.target !== el) return;
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', finish);
      window.clearTimeout(timer);
      fn();
    }
    el.addEventListener('transitionend', finish);
    timer = window.setTimeout(finish, 600);
  }

  function bump(el) {
    if (!el || reduceMotion.matches) return;
    el.classList.remove('is-bump');
    void el.offsetWidth;
    el.classList.add('is-bump');
  }

  function focusEl(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      el.focus();
    }
  }

  function listen(list, target, type, fn, options) {
    target.addEventListener(type, fn, options || false);
    list.push(function () {
      target.removeEventListener(type, fn, options || false);
    });
  }

  function onMediaChange(list, mq, fn) {
    if (mq.addEventListener) {
      mq.addEventListener('change', fn);
      list.push(function () {
        mq.removeEventListener('change', fn);
      });
    } else if (mq.addListener) {
      mq.addListener(fn);
      list.push(function () {
        mq.removeListener(fn);
      });
    }
  }

  /* ---------------------------------------------------------------- header */
  Shop5Parts.register('shop5-header', function (root) {
    var cleanup = [];
    var wrapper = root.querySelector('.header__search-bar-wrapper');
    var toggle = root.querySelector('[data-action="toggle-search"]');
    var count = root.querySelector('[data-shop5-header-count]');

    if (wrapper && toggle) {
      var syncSearch = function () {
        toggle.setAttribute('aria-expanded', wrapper.classList.contains('is-visible') ? 'true' : 'false');
      };
      var searchObserver = new MutationObserver(syncSearch);
      searchObserver.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
      cleanup.push(function () {
        searchObserver.disconnect();
      });
      syncSearch();

      // Escape closes the open phone drop (the vendor's full-screen state has its own Close button).
      listen(cleanup, document, 'keydown', function (event) {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        if (!wrapper.classList.contains('is-visible') || wrapper.classList.contains('is-fixed')) return;
        if (window.getComputedStyle(toggle).display === 'none') return;
        toggle.click();
        focusEl(toggle);
      });

      // Enter on the search icon (a click with detail 0, as a keyboard sends) that opens the drop puts the focus in
      // its field: the field comes before the icon in the page order, so Tab from the icon went on to the account
      // link and reached the field only backwards (WCAG 2.4.3; behave searchtab, stage 6H). A tap (detail 1 or more)
      // leaves the focus on the icon, so the drop shows as designed until the field is tapped. Runs before the
      // vendor's delegated toggle, so the drop is still closed here; the focus waits one task for it to open.
      listen(cleanup, toggle, 'click', function (event) {
        if (event.detail !== 0 || wrapper.classList.contains('is-visible')) return;
        window.setTimeout(function () {
          var field = wrapper.querySelector('.search-bar__input');
          if (field && wrapper.classList.contains('is-visible')) focusEl(field);
        }, 0);
      });
    }

    if (count) {
      var lastCount = count.textContent;
      var countObserver = new MutationObserver(function () {
        var now = count.textContent;
        if (now === lastCount) return;
        lastCount = now;
        bump(count);
      });
      countObserver.observe(count, { childList: true, characterData: true, subtree: true });
      cleanup.push(function () {
        countObserver.disconnect();
      });
      listen(cleanup, count, 'animationend', function () {
        count.classList.remove('is-bump');
      });
    }

    // Mini cart (vendor Cart, 641 px and up). The vendor CSS hangs the 470 px panel from the cart item's end edge with
    // its pointer 13 px in from that edge, made for the vendor's 44 px icon slot. The 6H cart action also carries a
    // label, and from 641 to 768 px the actions stand mid-row (the search field ends the row), so the pointer missed
    // the icon and the panel left the screen (behaviour check "minicart", 6H run). Each time the panel opens or the
    // vendor redraws it, the panel is kept 16 px inside the screen and its pointer is centred under the cart icon, in
    // logical offsets from the measured boxes. Up to 640 px the vendor's phone panel is left as it is. The offsets reach
    // assets/shop5-ui.css as --shop5-mini-start and --shop5-mini-pointer, applied while the panel carries the class
    // shop5-mini-placed.
    var mini = root.querySelector('#mini-cart');
    var cartIcon = root.querySelector('.header__cart-icon');
    var lap = window.matchMedia('(min-width: 641px)');

    if (mini && cartIcon) {
      var placeMini = function () {
        // A closing panel keeps its place while it fades out (0.25 s); the next opening places it again.
        if (lap.matches && mini.getAttribute('aria-hidden') !== 'false') return;
        var pointer = mini.querySelector('.icon--nav-triangle-borderless');
        // Back to the vendor's place first: the measures below start from it.
        mini.classList.remove('shop5-mini-placed');
        mini.style.removeProperty('--shop5-mini-start');
        mini.style.removeProperty('--shop5-mini-pointer');
        if (!lap.matches || !mini.offsetParent) return;
        var rtl = window.getComputedStyle(mini).direction === 'rtl';
        var item = mini.offsetParent.getBoundingClientRect();
        var width = mini.offsetWidth;
        var room = document.documentElement.clientWidth;
        var left = Math.max(16, Math.min(item.left + mini.offsetLeft, room - 16 - width));
        mini.style.setProperty('--shop5-mini-start', Math.round(rtl ? item.right - left - width : left - item.left) + 'px');
        if (pointer) {
          var icon = cartIcon.getBoundingClientRect();
          var centre = icon.left + icon.width / 2;
          var half = parseFloat(window.getComputedStyle(pointer).width) / 2 || 9;
          mini.style.setProperty('--shop5-mini-pointer', Math.round((rtl ? left + width - centre : centre - left) - half) + 'px');
        }
        mini.classList.add('shop5-mini-placed');
      };
      var miniObserver = new MutationObserver(placeMini);
      miniObserver.observe(mini, { attributes: true, attributeFilter: ['aria-hidden'], childList: true });
      cleanup.push(function () {
        miniObserver.disconnect();
      });
      listen(cleanup, window, 'resize', placeMini);
      onMediaChange(cleanup, lap, placeMini);
    }

    return function destroy() {
      cleanup.forEach(function (fn) {
        fn();
      });
    };
  });

  /* ---------------------------------------------------------------- menu panels */
  Shop5Parts.register('shop5-menu', function (root) {
    var cleanup = [];
    var html = document.documentElement;
    var desk = root.querySelector('[data-shop5-menu-panel="desktop"]');
    var mob = root.querySelector('[data-shop5-menu-panel="mobile"]');
    var deskInner = desk ? desk.querySelector('.shop5-mdesk__panel') : null;
    var mobInner = mob ? mob.querySelector('.shop5-mnav__panel') : null;
    var mobL1 = mob ? mob.querySelector('[data-shop5-menu-view="l1"]') : null;
    var desktop = window.matchMedia('(min-width: 769px)');
    var state = { open: null, opener: null, lockedByUs: false, l2: null, l2Opener: null, selected: false };
    var api = null;

    function setExpanded(value) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-shop5-open-menu]'), function (button) {
        button.setAttribute('aria-expanded', value);
      });
    }

    function focusables(scope) {
      var list = scope.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])');
      return Array.prototype.filter.call(list, function (el) {
        return el.getClientRects().length > 0 && !el.closest('[inert]');
      });
    }

    // The desktop panel hangs 12 px under the header; its backdrop starts at the header's bottom.
    function measure() {
      var header = document.querySelector('.shop5-header');
      var bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 84;
      if (bottom < 0) bottom = 0;
      desk.style.setProperty('--shop5-mdesk-hd', bottom + 'px');
      desk.style.setProperty('--shop5-mdesk-top', bottom + 12 + 'px');
    }

    function resetL2() {
      if (!mob) return;
      mob.classList.remove('is-l2');
      Array.prototype.forEach.call(mob.querySelectorAll('.shop5-mnav__view--l2'), function (view) {
        view.classList.remove('is-on');
        view.setAttribute('inert', '');
        view.hidden = true;
      });
      Array.prototype.forEach.call(mob.querySelectorAll('[data-shop5-menu-go]'), function (row) {
        row.setAttribute('aria-expanded', 'false');
      });
      if (mobL1) mobL1.removeAttribute('inert');
      state.l2 = null;
      state.l2Opener = null;
    }

    function hidePanel(panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('inert', '');
      panel.hidden = true;
      if (panel === mob) resetL2();
    }

    function open(kind, options) {
      options = options || {};
      var panel = kind === 'desktop' ? desk : mob;
      if (!panel) return;
      if (state.open === panel) {
        if (options.focusSearch) focusEl(mob.querySelector('[data-shop5-menu-search]'));
        return;
      }
      if (state.open) close({ restoreFocus: false, instant: true });
      state.opener = options.opener || document.activeElement;
      if (panel === desk) measure();
      panel.hidden = false;
      panel.removeAttribute('inert');
      if (panel === mob && !html.classList.contains('is-locked')) {
        html.classList.add('is-locked');
        state.lockedByUs = true;
      }
      html.classList.add('shop5-menu-open');
      state.open = panel;
      setExpanded('true');
      // The search field is focused inside the tap itself, so phones bring up the keyboard.
      if (options.focusSearch && panel === mob) {
        focusEl(mob.querySelector('[data-shop5-menu-search]'));
      } else if (panel === mob) {
        focusEl(mobInner);
      } else {
        var first = focusables(deskInner)[0];
        focusEl(first || deskInner);
      }
      nextFrame(function () {
        if (state.open === panel) panel.classList.add('is-open');
      });
    }

    function close(options) {
      options = options || {};
      var panel = state.open;
      if (!panel) return;
      var active = document.activeElement;
      var restore = options.restoreFocus !== false && (panel.contains(active) || active === document.body || !active);
      state.open = null;
      panel.classList.remove('is-open');
      panel.setAttribute('inert', '');
      setExpanded('false');
      html.classList.remove('shop5-menu-open');
      if (state.lockedByUs) {
        html.classList.remove('is-locked');
        state.lockedByUs = false;
      }
      if (restore && state.opener && document.documentElement.contains(state.opener)) focusEl(state.opener);
      if (options.instant) {
        hidePanel(panel);
        return;
      }
      afterTransition(panel === desk ? deskInner : mobInner, function () {
        if (state.open !== panel) hidePanel(panel);
      });
    }

    function showL2(row) {
      var id = row.getAttribute('data-shop5-menu-go');
      var view = mob.querySelector('[data-shop5-menu-view="' + id + '"]');
      if (!view) return;
      if (state.l2 && state.l2 !== view) {
        state.l2.classList.remove('is-on');
        state.l2.setAttribute('inert', '');
        state.l2.hidden = true;
      }
      view.hidden = false;
      view.removeAttribute('inert');
      view.scrollTop = 0;
      if (mobL1) mobL1.setAttribute('inert', '');
      row.setAttribute('aria-expanded', 'true');
      state.l2 = view;
      state.l2Opener = row;
      mob.classList.add('is-l2');
      focusEl(mob.querySelector('[data-shop5-menu-back]'));
      nextFrame(function () {
        if (state.l2 === view) view.classList.add('is-on');
      });
    }

    function back() {
      var view = state.l2;
      if (!view) return;
      var row = state.l2Opener;
      state.l2 = null;
      state.l2Opener = null;
      mob.classList.remove('is-l2');
      view.classList.remove('is-on');
      view.setAttribute('inert', '');
      if (mobL1) mobL1.removeAttribute('inert');
      if (row) {
        row.setAttribute('aria-expanded', 'false');
        focusEl(row);
      }
      afterTransition(view, function () {
        if (state.l2 !== view) view.hidden = true;
      });
    }

    function trap(event) {
      var scope = state.open === mob ? mobInner : deskInner;
      var list = focusables(scope);
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (!scope.contains(active)) {
        event.preventDefault();
        focusEl(event.shiftKey ? last : first);
      } else if (event.shiftKey && (active === first || active === scope)) {
        event.preventDefault();
        focusEl(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusEl(first);
      }
    }

    listen(cleanup, document, 'click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var opener = target.closest('[data-shop5-open-menu]');
      if (opener) {
        event.preventDefault();
        var kind = desktop.matches ? 'desktop' : 'mobile';
        if (state.open && state.open === (kind === 'desktop' ? desk : mob)) close();
        else open(kind, { opener: opener });
        return;
      }
      var searchOpener = target.closest('[data-shop5-open-search]');
      if (searchOpener) {
        event.preventDefault();
        open('mobile', { opener: searchOpener, focusSearch: true });
        return;
      }
      if (!state.open) return;
      if (state.open.contains(target) && target.closest('[data-shop5-menu-close]')) {
        close();
        return;
      }
      if (state.open === mob) {
        var go = target.closest('[data-shop5-menu-go]');
        if (go && mob.contains(go)) {
          event.preventDefault();
          showL2(go);
          return;
        }
        if (target.closest('[data-shop5-menu-back]') && mob.contains(target)) back();
        return;
      }
      // Desktop: a click anywhere outside the panel closes it (the backdrop is handled above).
      if (state.open === desk && !deskInner.contains(target)) close({ restoreFocus: false });
    });

    listen(cleanup, document, 'keydown', function (event) {
      if (!state.open) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (state.open === mob && state.l2) back();
        else close();
        return;
      }
      if (event.key === 'Tab') trap(event);
    });

    // Crossing 769 px with a panel open: the other layout's panel is the right one, so close at once.
    onMediaChange(cleanup, desktop, function () {
      if (state.open) close({ restoreFocus: false, instant: true });
    });

    // Back/forward cache: a page restored with a panel open comes back closed and unlocked.
    listen(cleanup, window, 'pageshow', function (event) {
      if (event.persisted && state.open) close({ restoreFocus: false, instant: true });
    });

    // Theme editor: selecting the section or one of its blocks shows the panel of the current width.
    function editorSelect(event) {
      if (!event.target || !event.target.contains || !event.target.contains(root)) return;
      state.selected = true;
      open(desktop.matches ? 'desktop' : 'mobile', { opener: null });
    }
    function editorDeselect(event) {
      if (!event.target || !event.target.contains || !event.target.contains(root)) return;
      state.selected = false;
      close({ restoreFocus: false });
    }
    listen(cleanup, document, 'shopify:section:select', editorSelect);
    listen(cleanup, document, 'shopify:block:select', editorSelect);
    listen(cleanup, document, 'shopify:section:deselect', editorDeselect);

    api = {
      open: function (options) {
        options = options || {};
        open(options.panel || (desktop.matches ? 'desktop' : 'mobile'), options);
      },
      close: function () {
        close();
      }
    };
    window.Shop5Menu = api;

    return function destroy() {
      if (state.open) close({ restoreFocus: false, instant: true });
      cleanup.forEach(function (fn) {
        fn();
      });
      setExpanded('false');
      if (window.Shop5Menu === api) delete window.Shop5Menu;
    };
  });

  /* ---------------------------------------------------------------- tab bar and toast */
  Shop5Parts.register('shop5-tabbar', function (root) {
    var cleanup = [];
    var bar = root.querySelector('.shop5-tabbar');
    var badges = root.querySelectorAll('[data-shop5-cart-count]');
    var status = root.querySelector('[data-shop5-cart-toast]');
    var alert = root.querySelector('[data-shop5-cart-toast-alert]');
    var api = null;

    // Hide on scroll down past 260 px, show on scroll up (design); only after the visitor's own input, so a scroll
    // the page makes by itself (restored position, anchor) does not hide it. Moves under 8 px are ignored.
    if (bar && root.hasAttribute('data-shop5-hide-on-scroll')) {
      var user = false;
      var lastY = window.pageYOffset;
      var markUser = function () {
        user = true;
      };
      // A keyboard user tabbing through the bar keeps it shown; focus a tap or a script left there does not.
      var keyboardInside = function () {
        try {
          return !!bar.querySelector(':focus-visible');
        } catch (error) {
          return false;
        }
      };
      ['wheel', 'touchmove', 'keydown', 'pointerdown'].forEach(function (type) {
        listen(cleanup, window, type, markUser, { passive: true });
      });
      listen(cleanup, window, 'scroll', function () {
        var y = window.pageYOffset;
        if (!user) {
          lastY = y;
          return;
        }
        if (Math.abs(y - lastY) < 8) return;
        if (y > lastY && y > 260 && !keyboardInside()) bar.classList.add('is-hidden');
        else if (y < lastY) bar.classList.remove('is-hidden');
        lastY = y;
      }, { passive: true });
    }

    // Cart badge: a copy of the vendor header count. The header section can be reloaded in the theme editor, which
    // replaces the count element, so the observer is bound again after every section load.
    var source = null;
    var observer = null;
    function copyCount() {
      if (!source) return;
      var text = source.textContent.trim();
      Array.prototype.forEach.call(badges, function (badge) {
        if (badge.textContent === text) return;
        badge.textContent = text;
        bump(badge);
      });
    }
    function bindCount() {
      if (observer) observer.disconnect();
      observer = null;
      source = document.querySelector('.header__cart-count');
      if (!source || !badges.length) return;
      observer = new MutationObserver(copyCount);
      observer.observe(source, { childList: true, characterData: true, subtree: true });
      copyCount();
    }
    bindCount();
    listen(cleanup, document, 'shopify:section:load', bindCount);
    Array.prototype.forEach.call(badges, function (badge) {
      listen(cleanup, badge, 'animationend', function () {
        badge.classList.remove('is-bump');
      });
    });
    cleanup.push(function () {
      if (observer) observer.disconnect();
    });

    // Without the menu section the Categories and Search tabs go to the catalogue and the search page.
    listen(cleanup, root, 'click', function (event) {
      if (window.Shop5Menu || !event.target.closest) return;
      var button = event.target.closest('[data-shop5-fallback]');
      if (button) window.location.href = button.getAttribute('data-shop5-fallback');
    });

    // Toast: role=status for news, role=alert for errors; 1.9 s (design), 4 s for an error so it can be read.
    var timers = { hide: 0, clear: 0 };
    function show(message, options) {
      options = options || {};
      var el = options.alert ? alert : status;
      var other = options.alert ? status : alert;
      if (!el) return;
      var text = message || (options.alert ? '' : status.getAttribute('data-shop5-cart-toast-default'));
      if (!text) return;
      var span = el.querySelector('[data-shop5-cart-toast-text]');
      window.clearTimeout(timers.hide);
      window.clearTimeout(timers.clear);
      if (other) other.classList.remove('is-shown');
      span.textContent = '';
      window.requestAnimationFrame(function () {
        span.textContent = String(text);
        el.classList.add('is-shown');
      });
      timers.hide = window.setTimeout(function () {
        el.classList.remove('is-shown');
        timers.clear = window.setTimeout(function () {
          span.textContent = '';
        }, 600);
      }, options.duration || (options.alert ? 4000 : 1900));
    }
    api = { show: show };
    window.Shop5Toast = api;
    cleanup.push(function () {
      window.clearTimeout(timers.hide);
      window.clearTimeout(timers.clear);
    });

    return function destroy() {
      cleanup.forEach(function (fn) {
        fn();
      });
      if (window.Shop5Toast === api) delete window.Shop5Toast;
    };
  });
})();

// 11. Footer link lists (stage 6H; docs/build/parts6h/shared-footer.js).
/* ---------------------------------------------------------------- footer link lists (sections/shop5-footer.liquid) */
/* Owner design footer script (index.html line 2232; spec E-seo-footer.md B4 and C2): from 769 px the two link lists
   are always open and their summary does not collapse them; below 769 px they are ordinary accordions that start
   closed. Driven by a media-query listener (the design ran once at load), so a resize or a rotation switches the mode.
   On desktop the summary also leaves the tab order (it does nothing there); the list links stay reachable. */
Shop5Parts.register('shared-footer', function (root) {
  var mq = window.matchMedia('(min-width: 769px)');
  var details = Array.prototype.slice.call(root.querySelectorAll('[data-shop5-ftm-acc] details'));
  var desktop = null;

  if (!details.length) {
    return function destroy() {};
  }

  function onSummaryClick(event) {
    if (mq.matches) {
      event.preventDefault();
    }
  }

  function apply() {
    var isDesktop = mq.matches;
    if (isDesktop === desktop) {
      return;
    }
    var first = desktop === null;
    desktop = isDesktop;
    details.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (isDesktop) {
        item.open = true;
        if (summary) {
          summary.setAttribute('tabindex', '-1');
        }
      } else {
        if (!first) {
          item.open = false;
        }
        if (summary) {
          summary.removeAttribute('tabindex');
        }
      }
    });
  }

  details.forEach(function (item) {
    var summary = item.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', onSummaryClick);
    }
  });

  apply();
  if (mq.addEventListener) {
    mq.addEventListener('change', apply);
  } else if (mq.addListener) {
    mq.addListener(apply);
  }

  return function destroy() {
    if (mq.removeEventListener) {
      mq.removeEventListener('change', apply);
    } else if (mq.removeListener) {
      mq.removeListener(apply);
    }
    details.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (summary) {
        summary.removeEventListener('click', onSummaryClick);
        summary.removeAttribute('tabindex');
      }
    });
  };
});

// 12. Language switcher (rules section 18, edit 1; snippets/shop5-lang-switcher.liquid, styles in shop5-ui.css section
//     36). The owner's component script (design/owner-lang/Shop5-Language-Switcher/language-switcher.html) as a part:
//     the button opens and closes the panel; a click outside it, Esc, a resize and the backdrop close it; while open on
//     desktop the panel follows the button on scroll. Build changes: the panel is the localization form, so choosing a
//     language submits it (Shopify returns the same page in that language) and the active one only closes the panel;
//     the panel's place goes to the stylesheet as --shop5-lang-top and --shop5-lang-start (measured from the screen's
//     reading-start edge, so one rule serves both directions; tc.mjs: no physical sides); focus moves to the active
//     row on open and back to the button when Esc closes; the theme editor's unload puts the moved nodes back.
Shop5Parts.register('shop5-lang', function (root) {
  var btn = root.querySelector('[data-shop5-lang-btn]');
  var pop = root.querySelector('.shop5-lang__pop');
  var bd = root.querySelector('[data-shop5-lang-bd]');
  if (!btn || !pop || !bd) {
    return function destroy() {};
  }
  var mq = window.matchMedia('(max-width: 768px)');
  var open = false;

  // Out of the sticky header's stacking context, above the tab bar and the menu panels (shop5-ui.css section 36).
  document.body.appendChild(bd);
  document.body.appendChild(pop);

  function place() {
    if (mq.matches) {
      pop.style.removeProperty('--shop5-lang-top');
      pop.style.removeProperty('--shop5-lang-start');
      return;
    }
    var r = btn.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    var rtl = window.getComputedStyle(pop).direction === 'rtl';
    var width = pop.offsetWidth;
    // The button's end edge, from the screen's reading-start edge; the panel's end edge lines up with it.
    var end = rtl ? vw - r.left : r.right;
    var start = Math.max(12, Math.min(end - width, vw - 12 - width));
    pop.style.setProperty('--shop5-lang-top', r.bottom + 8 + 'px');
    pop.style.setProperty('--shop5-lang-start', start + 'px');
  }

  function setOpen(value, returnFocus) {
    if (value) place();
    open = value;
    root.classList.toggle('is-open', value);
    pop.classList.toggle('is-open', value);
    bd.classList.toggle('is-open', value);
    btn.setAttribute('aria-expanded', value ? 'true' : 'false');
    if (value) {
      var target = pop.querySelector('.shop5-lang__opt.is-on') || pop.querySelector('.shop5-lang__opt');
      if (target) target.focus({ preventScroll: true });
    } else if (returnFocus) {
      btn.focus({ preventScroll: true });
    }
  }

  function onButton(event) {
    event.stopPropagation();
    setOpen(!open, false);
  }

  function onBackdrop() {
    setOpen(false, false);
  }

  function onDocClick(event) {
    if (open && !pop.contains(event.target) && !root.contains(event.target)) setOpen(false, false);
  }

  function onKey(event) {
    if (open && event.key === 'Escape') setOpen(false, pop.contains(document.activeElement));
  }

  function onResize() {
    if (open) setOpen(false, false);
  }

  function onScroll() {
    if (open && !mq.matches) place();
  }

  function onPopClick(event) {
    var opt = event.target.closest ? event.target.closest('.shop5-lang__opt') : null;
    // The page is already in this language: nothing to submit.
    if (opt && opt.classList.contains('is-on')) {
      event.preventDefault();
      setOpen(false, true);
    }
  }

  btn.addEventListener('click', onButton);
  bd.addEventListener('click', onBackdrop);
  pop.addEventListener('click', onPopClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onScroll, { passive: true });

  return function destroy() {
    btn.removeEventListener('click', onButton);
    bd.removeEventListener('click', onBackdrop);
    pop.removeEventListener('click', onPopClick);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    root.appendChild(bd);
    root.appendChild(pop);
  };
});

// 13. Quick add. form[data-shop5-quick-add] (one per one-variant product card, home page only): fetch to routes.cart_add_url
// + '.js'; success: class is-added (icon swap), label "Added" for 2.4 s, window.Shop5Toast.show(text), then the vendor
// Cart re-renders the count and the mini cart on 'cart:refresh' (the drawer stays closed: product:added is never sent);
// refused add: the cart's own description in the toast as an alert. Without script the form posts and Shopify adds.
(function () {
  'use strict';

  var toast = function (text, alert) {
    if (text && window.Shop5Toast && typeof window.Shop5Toast.show === 'function') {
      window.Shop5Toast.show(text, alert ? { alert: true } : undefined);
    }
  };

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-shop5-quick-add]')) return;
    var button = form.querySelector('.shop5-card__btn');
    var label = form.querySelector('.shop5-card__label');
    if (!button || !label) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true' || button.classList.contains('is-added')) return;
    var original = label.textContent;
    button.setAttribute('aria-busy', 'true');
    fetch(form.getAttribute('action') + '.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        button.removeAttribute('aria-busy');
        if (!result.ok) {
          toast((result.data && (result.data.description || result.data.message)) || form.getAttribute('data-error'), true);
          return;
        }
        button.classList.add('is-added');
        label.textContent = form.getAttribute('data-added') || original;
        toast(form.getAttribute('data-toast'), false);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
        window.setTimeout(function () {
          button.classList.remove('is-added');
          label.textContent = original;
        }, 2400);
      })
      .catch(function () {
        button.removeAttribute('aria-busy');
        toast(form.getAttribute('data-error'), true);
      });
  });
})();
